<script setup>
import { onMounted, ref, defineOptions } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import cityAudiosAPI from 'dashboard/api/cityAudios';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';

defineOptions({ name: 'CityAudiosSettings' });

const { t } = useI18n();

const records = ref([]);
const isLoading = ref(false);
const showForm = ref(false);
const showDelete = ref(false);
const active = ref({});
const form = ref({ city: '', matchCodes: '', active: true, audio: null });

const fetchRecords = async () => {
  isLoading.value = true;
  try {
    const { data } = await cityAudiosAPI.get();
    records.value = data.payload;
  } finally {
    isLoading.value = false;
  }
};

onMounted(fetchRecords);

const openForm = record => {
  active.value = record || {};
  form.value = {
    city: record?.city || '',
    matchCodes: (record?.match_codes || []).join(', '),
    active: record ? record.active : true,
    audio: null,
  };
  showForm.value = true;
};

const onFileChange = event => {
  form.value.audio = event.target.files[0];
};

const save = async () => {
  const payload = new FormData();
  payload.append('city_audio[city]', form.value.city);
  payload.append('city_audio[active]', form.value.active);
  const codes = form.value.matchCodes
    .split(',')
    .map(code => code.trim())
    .filter(Boolean);
  if (codes.length) {
    codes.forEach(code => payload.append('city_audio[match_codes][]', code));
  } else {
    payload.append('city_audio[match_codes][]', '');
  }
  if (form.value.audio) payload.append('city_audio[audio]', form.value.audio);

  try {
    if (active.value.id) {
      await cityAudiosAPI.update(active.value.id, payload);
    } else {
      await cityAudiosAPI.create(payload);
    }
    showForm.value = false;
    useAlert(t('CITY_AUDIOS.SAVE_SUCCESS'));
    fetchRecords();
  } catch (error) {
    useAlert(error?.response?.data?.message || t('CITY_AUDIOS.SAVE_ERROR'));
  }
};

const openDelete = record => {
  active.value = record;
  showDelete.value = true;
};

const confirmDelete = async () => {
  showDelete.value = false;
  try {
    await cityAudiosAPI.delete(active.value.id);
    useAlert(t('CITY_AUDIOS.DELETE_SUCCESS'));
    fetchRecords();
  } catch (error) {
    useAlert(t('CITY_AUDIOS.DELETE_ERROR'));
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('CITY_AUDIOS.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('CITY_AUDIOS.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('CITY_AUDIOS.HEADER')"
        :description="$t('CITY_AUDIOS.DESCRIPTION')"
      >
        <template #actions>
          <Button
            :label="$t('CITY_AUDIOS.ADD')"
            size="sm"
            @click="openForm()"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <ul class="flex flex-col">
        <li
          v-for="item in records"
          :key="item.id"
          class="flex flex-col gap-3 py-4 border-b border-n-weak md:flex-row md:items-center md:justify-between md:gap-6"
        >
          <div class="flex flex-col gap-1 min-w-0 md:w-1/3">
            <span class="text-heading-3 text-n-slate-12 truncate">
              {{ item.city }}
              <span v-if="!item.active" class="ml-2 text-n-slate-11">
                {{ $t('CITY_AUDIOS.INACTIVE') }}
              </span>
            </span>
            <span class="text-body-main text-n-slate-11 break-words">
              {{ item.match_codes.join(', ') }}
            </span>
          </div>
          <audio
            v-if="item.audio_url"
            :src="item.audio_url"
            controls
            class="w-full md:w-72 md:flex-shrink-0"
          />
          <div class="flex gap-3 justify-end flex-shrink-0">
            <Button icon="i-woot-edit-pen" slate sm @click="openForm(item)" />
            <Button
              icon="i-woot-bin"
              slate
              sm
              class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
              @click="openDelete(item)"
            />
          </div>
        </li>
      </ul>
    </template>

    <woot-modal v-model:show="showForm" :on-close="() => (showForm = false)">
      <div class="flex flex-col gap-4 p-4 md:p-8">
        <h3 class="text-heading-2 text-n-slate-12">
          {{ active.id ? $t('CITY_AUDIOS.EDIT') : $t('CITY_AUDIOS.ADD') }}
        </h3>
        <label class="flex flex-col gap-1 text-n-slate-12">
          {{ $t('CITY_AUDIOS.FORM.CITY') }}
          <input v-model="form.city" type="text" />
        </label>
        <label class="flex flex-col gap-1 text-n-slate-12">
          {{ $t('CITY_AUDIOS.FORM.CODES') }}
          <input v-model="form.matchCodes" type="text" />
          <span class="text-body-main text-n-slate-11">
            {{ $t('CITY_AUDIOS.FORM.CODES_HELP') }}
          </span>
        </label>
        <label class="flex flex-col gap-1 text-n-slate-12">
          {{ $t('CITY_AUDIOS.FORM.AUDIO') }}
          <input type="file" accept="audio/*" @change="onFileChange" />
          <span class="text-body-main text-n-slate-11">
            {{ $t('CITY_AUDIOS.FORM.AUDIO_HELP') }}
          </span>
        </label>
        <label class="flex items-center gap-2 text-n-slate-12">
          <input v-model="form.active" type="checkbox" class="m-0" />
          {{ $t('CITY_AUDIOS.FORM.ACTIVE') }}
        </label>
        <div class="flex justify-end gap-2">
          <Button
            :label="$t('CITY_AUDIOS.CANCEL')"
            slate
            faded
            @click="showForm = false"
          />
          <Button :label="$t('CITY_AUDIOS.SAVE')" @click="save" />
        </div>
      </div>
    </woot-modal>

    <woot-confirm-delete-modal
      v-if="showDelete"
      v-model:show="showDelete"
      :title="$t('CITY_AUDIOS.DELETE_CONFIRM', { city: active.city })"
      :message="$t('CITY_AUDIOS.DELETE_MESSAGE')"
      :confirm-text="$t('CITY_AUDIOS.DELETE')"
      :reject-text="$t('CITY_AUDIOS.CANCEL')"
      @on-confirm="confirmDelete"
      @on-close="showDelete = false"
    />
  </SettingsLayout>
</template>
